import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users, Mail, Copy, Check, Trash2, Clock, UserCheck, UserX, Loader2, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';


const STATUS_STYLE = {
  pending: { icon: Clock, labelKey: 'share_plan.pending', color: '#f59e0b' },
  accepted: { icon: UserCheck, labelKey: 'share_plan.accepted', color: '#10b981' },
  denied: { icon: UserX, labelKey: 'share_plan.denied', color: '#ef4444' },
};

export default function SharePlanManager() {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [inviteLink, setInviteLink] = useState(null);
  const [emails, setEmails] = useState(['', '', '', '']);
  const [saving, setSaving] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const emailsEditedRef = useRef(false);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/share-plan/members`, { withCredentials: true });
      setMembers(res.data.members || []);
      setInviteLink(res.data.invite_link);

      // Only update email slots if user hasn't edited them
      if (!emailsEditedRef.current) {
        const existingEmails = (res.data.members || []).map((m) => m.email);
        const padded = [...existingEmails, '', '', '', ''].slice(0, 4);
        setEmails(padded);
      }
    } catch {
      // Not on share plan
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
    // Poll for member status updates every 15s
    const interval = setInterval(fetchMembers, 15000);
    return () => clearInterval(interval);
  }, [fetchMembers]);

  const handleSave = async () => {
    const nonEmpty = emails.filter((e) => e.trim());
    // Basic email validation
    const invalid = nonEmpty.find((e) => !e.includes('@'));
    if (invalid) {
      toast.error(`Invalid email: ${invalid}`);
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/share-plan/update-members`, { emails: nonEmpty }, { withCredentials: true });
      emailsEditedRef.current = false;
      toast.success(t('generated.shareplanmanager_members_updated_9fda'));
      await fetchMembers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('share_plan.failed_update'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (email) => {
    try {
      await axios.delete(`${API}/share-plan/members/${encodeURIComponent(email)}`, { withCredentials: true });
      emailsEditedRef.current = false;
      toast.success(t('share_plan.member_removed'));
      await fetchMembers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('share_plan.failed_remove'));
    }
  };

  const handleGetLink = async () => {
    try {
      const res = await axios.get(`${API}/share-plan/invite-link`, { withCredentials: true });
      const code = res.data.link_code;
      setInviteLink(code);
      const url = `${window.location.origin}/api/og/invite/${code}`;
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      toast.success(t('share_plan.link_copied'));
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      toast.error(t('share_plan.failed_generate'));
    }
  };

  const handleCopyLink = async () => {
    if (!inviteLink) return;
    const url = `${window.location.origin}/api/og/invite/${inviteLink}`;
    await navigator.clipboard.writeText(url);
    setLinkCopied(true);
    toast.success(t('share_plan.link_copied'));
    setTimeout(() => setLinkCopied(false), 2500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: 'var(--theme-accent)' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="share-plan-manager">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#10b98122' }}>
          <Users className="h-5 w-5" style={{ color: '#10b981' }} />
        </div>
        <div>
          <h3 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>
            {t('share_plan.title')}
          </h3>
          <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('share_plan.subtitle')}
          </p>
        </div>
      </div>
      {/* Invite Link */}
      <div
        className="rounded-xl p-4"
        style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Link2 className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
          <span className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{t('share_plan.invite_link')}</span>
        </div>
        <p className="text-xs mb-3" style={{ color: 'var(--theme-textSecondary)' }}>
          {t('share_plan.invite_link_desc')}
        </p>
        {inviteLink ? (
          <div className="flex items-center gap-2">
            <code
              className="flex-1 text-xs px-3 py-2 rounded-lg truncate"
              style={{ background: 'var(--theme-background)', color: 'var(--theme-text)', border: '1px solid var(--glass-border)' }}
              data-testid="invite-link-display"
            >
              {window.location.origin}/api/og/invite/{inviteLink}
            </code>
            <Button
              size="sm"
              onClick={handleCopyLink}
              className="rounded-lg shrink-0"
              style={{ background: 'var(--theme-accent)', color: '#fff' }}
              data-testid="copy-invite-link-btn"
            >
              {linkCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            onClick={handleGetLink}
            className="rounded-lg gap-2"
            style={{ background: 'var(--theme-accent)', color: '#fff' }}
            data-testid="generate-invite-link-btn"
          >
            <Link2 className="h-4 w-4" /> {t('share_plan.generate_link')}
          </Button>
        )}
      </div>
      {/* Email Slots */}
      <div
        className="rounded-xl p-4 space-y-3"
        style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
      >
        <div className="flex items-center gap-2 mb-1">
          <Mail className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />
          <span className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{t('share_plan.member_emails')}</span>
        </div>
        {emails.map((email, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs w-5 text-center" style={{ color: 'var(--theme-textSecondary)' }}>{i + 1}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                emailsEditedRef.current = true;
                const next = [...emails];
                next[i] = e.target.value;
                setEmails(next);
              }}
              placeholder={t('generated.shareplanmanager_member_email_com_cdcb')}
              className="flex-1 text-sm px-3 py-2 rounded-lg outline-none"
              style={{
                background: 'var(--theme-background)',
                color: 'var(--theme-text)',
                border: '1px solid var(--glass-border)',
              }}
              data-testid={`member-email-${i}`}
            />
          </div>
        ))}
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="rounded-lg gap-2 mt-2"
          style={{ background: '#10b981', color: '#fff' }}
          data-testid="save-members-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          {t('share_plan.save_members')}
        </Button>
      </div>
      {/* Member Status List */}
      {members.length > 0 && (
        <div
          className="rounded-xl p-4 space-y-2"
          style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
        >
          <span className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>{t('share_plan.status')}</span>
          {members.map((m) => {
            const s = STATUS_STYLE[m.status] || STATUS_STYLE.pending;
            const StatusIcon = s.icon;
            return (
              <div
                key={m.email}
                className="flex items-center justify-between py-2 px-3 rounded-lg"
                style={{ background: 'var(--theme-background)' }}
                data-testid={`member-row-${m.email}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <StatusIcon className="h-4 w-4 shrink-0" style={{ color: s.color }} />
                  <div className="min-w-0">
                    <p className="text-sm truncate" style={{ color: 'var(--theme-text)' }}>
                      {m.display_name || m.email}
                    </p>
                    {m.display_name && (
                      <p className="text-[11px] truncate" style={{ color: 'var(--theme-textSecondary)' }}>{m.email}</p>
                    )}
                  </div>
                  <span
                    className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full shrink-0"
                    style={{ background: `${s.color}22`, color: s.color }}
                  >
                    {t(s.labelKey)}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(m.email)}
                  className="h-8 w-8 p-0 rounded-full shrink-0 hover:bg-red-500/10"
                  data-testid={`remove-member-${m.email}`}
                >
                  <Trash2 className="h-4 w-4" style={{ color: '#ef4444' }} />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
